import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Typography,
  Divider,
  Space,
  Popconfirm,
  message,
  Modal,
  notification,
} from 'antd';
import _ from 'lodash';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ShopStore, CurrentUserStore, InspectionsStore } from 'stores';
import FleetDetails from './common/FleetDetails';
import FormDetails from './common/FormDetails';
import FormGroup from './common/FormGroup';
import { Logger } from 'stores/Classes';
import { extractIssuesSorted } from './common/utils';

const ResponsiveEditField = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  .edit-template-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: #f5f6fa;
    min-height: 100vh;
    padding: 32px;
    transition:
      max-width 0.2s,
      padding 0.2s;
  }
  @media (max-width: 800px) {
    padding: 0;
    .edit-template-inner {
      max-width: 90vw;
      padding: 8px;
    }
    .ant-row {
      flex-direction: column;
    }
    .ant-col {
      width: 100%;
      max-width: 100%;
    }
    .checkbox-row,
    .checkbox-row.ant-row {
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
    }
    .checkbox-row .ant-col,
    .checkbox-row.ant-row .ant-col {
      width: 25% !important;
      max-width: 25% !important;
    }
    .checkbox-label-text {
      display: none !important;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    .save-wo-btn {
      flex-basis: 100%;
      margin-top: 8px;
      margin-left: 0;
    }
  }
`;

const { Title, Text, Link } = Typography;

const EditForm = () => {
  const [form] = Form.useForm();
  let history = useHistory();
  let { formId } = useParams();
  const unblockRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const [inspectionForm, setInspectionForm] = useState({});

  const [formTitle, setFormTitle] = useState('Inspection Form 1');

  const [fleetAddress, setFleetAddress] = useState('');
  const [fleetLogoURL, setFleetLogoURL] = useState('');

  const [groups, setGroups] = useState([]);
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      await loadData(ShopStore.currentShop.id);
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!isDirty) return;

    unblockRef.current = history.block((location, action) => {
      Modal.confirm({
        title: 'Save Changes',
        content: 'Do you want to save changes before exiting?',
        okText: 'Save & Exit',
        cancelText: 'Exit Without Saving',
        onOk: async () => {
          unblockRef.current?.();
          await handleSave(false);
          history.push(location.pathname);
        },
        onCancel: () => {
          unblockRef.current?.();
          history.push(location.pathname);
        },
        okCancel: true,
        closable: true,
        maskClosable: true,
        cancelButtonProps: { danger: true },
      });

      return false;
    });

    return () => {
      unblockRef.current?.();
    };
  }, [isDirty, history]);

  const loadData = async (shopId) => {
    try {
      const shop = await ShopStore.getShopById(shopId);

      const address = _.get(shop, 'address', '');
      const logoUrl = _.get(shop, 'logoUrl', '');

      setFleetAddress(address);
      setFleetLogoURL(logoUrl);

      const carrentForm = await InspectionsStore.getInspectionForm(
        shopId,
        formId
      );
      setFormTitle(carrentForm[0].title);
      setGroups(carrentForm[0].groups);
      setInspectionForm(carrentForm[0]);

      setTimeout(() => {
        form.setFieldsValue({
          createdBy: carrentForm[0].created_by,
          assetType: carrentForm[0].asset_type,
          asset: carrentForm[0].asset_id,
          vin: carrentForm[0].vin,
          mileage: carrentForm[0].mileage,
          engineHours: carrentForm[0].engine_hours,
          createdAt: dayjs(carrentForm[0].created_at),
        });
      }, 500);
    } catch (e) {
      Logger.log(e);
    }
  };

  const handleSave = async (stay = true) => {
    const allValues = await form.validateFields();

    const mergedValues = JSON.parse(JSON.stringify(allValues));

    if (mergedValues.groups && groups) {
      mergedValues.groups.forEach((group, gIdx) => {
        const sourceGroup = groups[gIdx] || {};
        group.id = sourceGroup.id;
        group.title = sourceGroup.title;

        if (!group.sections) return;
        group.sections.forEach((section, sIdx) => {
          const sourceSection = sourceGroup.sections?.[sIdx] || {};
          section.checkboxImportant = sourceSection.checkboxImportant || [];
          section.checkboxLabels = sourceSection.checkboxLabels || [];
          section.description = sourceSection.description || '';
          section.freeTextLabel = sourceSection.freeTextLabel || '';

          if (section.images && Array.isArray(section.images)) {
            const uploadedUrls = [];

            for (const img of section.images) {
              if (img.url) {
                uploadedUrls.push(img.url);
              }
            }

            section.images = uploadedUrls;
          }
        });
      });
    }

    const payload = {
      ...mergedValues,
      form_id: formId,
      title: formTitle,
    };

    try {
      const shop_id = ShopStore.currentShop.id;

      setIsDirty(false);

      const form = await InspectionsStore.updateForm(shop_id, payload);
      if (stay) {
        history.goBack();
      }

      notification.success({
        message: 'Success',
        description: 'Form updated successfully',
      });

      groups.forEach((group, gIdx) => {
        group.sections.forEach((section, sIdx) => {
          const importantFlags = section.checkboxImportant || [];
          const formSection = allValues.groups?.[gIdx]?.sections?.[sIdx];
          if (!formSection) return;
          (formSection.checkboxes || []).forEach((checked, idx) => {
            if (checked && importantFlags[idx]) {
              const label =
                formSection.checkboxLabels?.[idx] ||
                section.checkboxLabels?.[idx] ||
                `Checkbox ${idx + 1}`;
              const freeText = formSection.freeText || '';
            }
          });
        });
      });
    } catch (e) {
      console.log(e);
      return;
    }
  };

  const handleDownloadClick = async () => {
    const shopId = ShopStore.currentShop.id;
    const allValues = form.getFieldsValue(true);
    const mergedValues = JSON.parse(JSON.stringify(allValues));
    try {
      await InspectionsStore.downloadFormPdf(shopId, formId);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSaveWithWO = async () => {
    const allValues = form.getFieldsValue(true);

    const mergedValues = JSON.parse(JSON.stringify(allValues));

    if (mergedValues.groups && groups) {
      mergedValues.groups.forEach((group, gIdx) => {
        const sourceGroup = groups[gIdx] || {};
        group.id = sourceGroup.id;
        group.title = sourceGroup.title;

        if (!group.sections) return;
        group.sections.forEach((section, sIdx) => {
          const sourceSection = sourceGroup.sections?.[sIdx] || {};
          section.checkboxImportant = sourceSection.checkboxImportant || [];
          section.checkboxLabels = sourceSection.checkboxLabels || [];
          section.description = sourceSection.description || '';
          section.freeTextLabel = sourceSection.freeTextLabel || '';

          if (section.images && Array.isArray(section.images)) {
            const uploadedUrls = [];

            for (const img of section.images) {
              if (img.url) {
                uploadedUrls.push(img.url);
              }
            }

            section.images = uploadedUrls;
          }
        });
      });
    }

    const payload = {
      ...mergedValues,
      form_id: formId,
      title: formTitle,
    };

    try {
      const shop_id = ShopStore.currentShop.id;

      setIsDirty(false);

      const form = await InspectionsStore.updateForm(shop_id, payload);

      notification.success({
        message: 'Success',
        description: 'Form updated successfully',
      });

      const issues = extractIssuesSorted(form.groups);

      const formData = {
        ...allValues,
        formId: formId,
        formTitle: formTitle,
        issues,
      };

      if (inspectionForm?.work_order_id) {
        history.push(`/work-order/${inspectionForm?.work_order_id}/edit`, {
          data: formData,
        });
      } else {
        history.push('/work-order/add', {
          data: formData,
        });
      }

      // I NEED IT!
      // groups.forEach((group, gIdx) => {
      //   group.sections.forEach((section, sIdx) => {
      //     const importantFlags = section.checkboxImportant || [];
      //     const formSection = allValues.groups?.[gIdx]?.sections?.[sIdx];
      //     if (!formSection) return;
      //     (formSection.checkboxes || []).forEach((checked, idx) => {
      //       if (checked && importantFlags[idx]) {
      //         const label =
      //           formSection.checkboxLabels?.[idx] ||
      //           section.checkboxLabels?.[idx] ||
      //           `Checkbox ${idx + 1}`;
      //         const freeText = formSection.freeText || '';
      //         console.log(
      //           `Group ${gIdx + 1}, Section ${sIdx + 1}, Checkbox ${idx + 1}: "${label}" -- REQUIRES ATTENTION. Free text: "${freeText}"`
      //         );
      //       }
      //     });
      //   });
      // });
    } catch (e) {
      console.log(e);
      return;
    }
  };

  const saveWithWOBtn = inspectionForm?.work_order_id
    ? 'Save & Update Work Order'
    : 'Save & Create Work Order';

  return (
    <ResponsiveEditField>
      <div className="edit-template-inner">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3}>{formTitle}</Title>
          </Col>
          <Col>
            <ButtonRow>
              {!editable && <Button type="default">Edit</Button>}
              <Button
                type="default"
                style={{
                  background: 'rgba(255, 77, 79, 0.2)',
                  borderColor: 'rgba(255, 77, 79, 0.0)',
                }}
                danger
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                disabled={!editable}
              >
                Save & Exit
              </Button>
              <Button type="primary" onClick={handleSaveWithWO}>
                {saveWithWOBtn}
              </Button>
              <Button type="primary" ghost onClick={handleDownloadClick}>
                <PrinterOutlined />
                Print
              </Button>
            </ButtonRow>
          </Col>
        </Row>

        <FleetDetails fleetLogoURL={fleetLogoURL} fleetAddress={fleetAddress} />
        <Form
          layout="vertical"
          form={form}
          onValuesChange={() => setIsDirty(true)}
        >
          <FormDetails form={form} />
          {groups.map((group, idx) => (
            <FormGroup
              key={group.id}
              group={group}
              groupIndex={idx}
              form={form}
            />
          ))}
        </Form>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col></Col>
          <Col>
            <ButtonRow>
              {!editable && <Button type="default">Edit</Button>}
              <Button
                type="default"
                style={{
                  background: 'rgba(255, 77, 79, 0.2)',
                  borderColor: 'rgba(255, 77, 79, 0.0)',
                }}
                danger
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                disabled={!editable}
              >
                Save & Exit
              </Button>
              <Button type="primary" onClick={handleSaveWithWO}>
                {saveWithWOBtn}
              </Button>
            </ButtonRow>
          </Col>
        </Row>
      </div>
    </ResponsiveEditField>
  );
};

export default EditForm;
